import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { Reseller, ResellerCreate, ResellerUpdate } from '@arcnet/contracts';

interface List<T> { data: T[] }

/**
 * A reseller's customer, as the reseller detail page needs it -- fewer fields
 * than a Tenant and one it does not have, so it is its own shape rather than a
 * partial Tenant that would go stale against the real one.
 */
export interface ResellerTenant {
  id: string;
  name: string;
  slug: string;
  sip_domain: string;
  status: string;
  max_concurrent_calls: number;
  extension_count: number;
  created_at: string;
}

export const resellerKeys = {
  all: (params?: unknown) => ['resellers', params] as const,
  one: (id: string) => ['reseller', id] as const,
  tenants: (id: string) => ['reseller-tenants', id] as const,
};

export function useResellers(params: { status?: string; search?: string } = {}) {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.search) sp.set('search', params.search);
  const q = sp.toString();
  return useQuery({
    queryKey: resellerKeys.all(params),
    queryFn: () => api.get<List<Reseller>>(`/v1/resellers${q ? `?${q}` : ''}`),
  });
}

export function useReseller(id: string | null) {
  return useQuery({
    queryKey: resellerKeys.one(id ?? ''),
    queryFn: () => api.get<Reseller>(`/v1/resellers/${id}`),
    enabled: !!id,
  });
}

export function useResellerTenants(id: string | null) {
  return useQuery({
    queryKey: resellerKeys.tenants(id ?? ''),
    queryFn: () => api.get<List<ResellerTenant>>(`/v1/resellers/${id}/tenants`),
    enabled: !!id,
  });
}

export function useCreateReseller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ResellerCreate) => api.post<Reseller>('/v1/resellers', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['resellers'] }),
  });
}

export function useUpdateReseller(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ResellerUpdate) => api.patch<Reseller>(`/v1/resellers/${id}`, body),
    onSuccess: () => {
      // Both the detail and the list: the list shows name and status, so an
      // edit that only invalidated the detail would leave the list stale behind
      // the user's back button.
      void qc.invalidateQueries({ queryKey: resellerKeys.one(id) });
      void qc.invalidateQueries({ queryKey: ['resellers'] });
    },
  });
}

/**
 * Archive. The server refuses while any customer or staff membership remains,
 * so the confirmation below does not need to warn about a cascade -- there
 * isn't one, and the refusal explains itself when it happens.
 */
export function useArchiveReseller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<null>(`/v1/resellers/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['resellers'] }),
  });
}
